import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  ChevronRight,
  Database,
  ExternalLink,
  FileText,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  Upload,
  X
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { bigqueryApi } from '../../api/bigqueryApi';
import { udfApi } from '../../api/udfApi';

const FunctionImportModal = ({
  isOpen,
  onClose,
  onImportComplete,
  onBillingRequired,
  importedFunctions = []
}) => {
  const [step, setStep] = useState('select'); // select | bigquery-auth | bigquery-dataset | bigquery-routine | bigquery-confirm
  const [searchQuery, setSearchQuery] = useState('');
  const [selection, setSelection] = useState({
    project: null,
    dataset: null,
    routines: []
  });
  const [bqStatus, setBqStatus] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [routines, setRoutines] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState(null);

  const resetModal = useCallback(() => {
    setStep('select');
    setSearchQuery('');
    setSelection({ project: null, dataset: null, routines: [] });
    setDatasets([]);
    setRoutines([]);
    setError(null);
  }, []);

  const checkBQStatus = useCallback(async () => {
    if (!isOpen) return;
    try {
      const status = await bigqueryApi.getStatus();
      setBqStatus(status);
    } catch (err) {
      console.error('Failed to check BigQuery status for function import:', err);
    }
  }, [isOpen]);

  const loadDatasets = useCallback(async (projectId) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await bigqueryApi.getDatasets(projectId);
      setDatasets(data || []);
      setStep('bigquery-dataset');
    } catch (err) {
      console.error('Failed to load BigQuery datasets for function import:', err);
      setError('Failed to load BigQuery datasets.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadRoutines = useCallback(async (projectId, datasetId) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await bigqueryApi.listRoutines(projectId, datasetId);
      setRoutines(data || []);
      setStep('bigquery-routine');
    } catch (err) {
      console.error('Failed to load BigQuery routines:', err);
      setError('Failed to load routines for this dataset.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleBQLanding = useCallback(async () => {
    if (bqStatus?.connected) {
      if (!bqStatus.billing_project_id) {
        if (onBillingRequired) onBillingRequired();
        else setStep('bigquery-auth');
      } else {
        const project = {
          project_id: bqStatus.billing_project_id,
          friendly_name: bqStatus.billing_project_id
        };
        setSelection(prev => ({ ...prev, project }));
        loadDatasets(bqStatus.billing_project_id);
      }
      return;
    }
    setStep('bigquery-auth');
  }, [bqStatus, loadDatasets, onBillingRequired]);

  useEffect(() => {
    if (!isOpen) return;
    resetModal();
    checkBQStatus();
  }, [isOpen, resetModal, checkBQStatus]);

  useEffect(() => {
    if (isOpen && bqStatus?.connected && step === 'bigquery-auth') {
      handleBQLanding();
    }
  }, [isOpen, bqStatus, step, handleBQLanding]);

  if (!isOpen) return null;

  const importedRoutineIds = new Set(importedFunctions.map(fn => fn.routine_id));

  const filteredDatasets = datasets.filter(d =>
    d.dataset_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredRoutines = routines.filter(r =>
    r.routine_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleBack = () => {
    if (step === 'bigquery-auth' || step === 'bigquery-dataset') setStep('select');
    else if (step === 'bigquery-routine') setStep('bigquery-dataset');
    else if (step === 'bigquery-confirm') setStep('bigquery-routine');
    setError(null);
  };

  const handleBQAuth = async () => {
    try {
      const { url } = await bigqueryApi.getLoginUrl();
      window.location.href = url;
    } catch (err) {
      console.error('Failed to get BigQuery login URL:', err);
      setError('Failed to start BigQuery authentication.');
    }
  };

  const handleDatasetSelect = (dataset) => {
    setSelection(prev => ({ ...prev, dataset, routines: [] }));
    loadRoutines(selection.project.project_id, dataset.dataset_id);
  };

  const toggleRoutineSelection = (routine) => {
    const fullRoutineId = `${selection.project.project_id}.${selection.dataset.dataset_id}.${routine.routine_id}`;
    if (importedRoutineIds.has(fullRoutineId)) return;

    setSelection(prev => {
      const exists = prev.routines.some(r => r.fullRoutineId === fullRoutineId);
      if (exists) {
        return {
          ...prev,
          routines: prev.routines.filter(r => r.fullRoutineId !== fullRoutineId)
        };
      }
      return {
        ...prev,
        routines: [
          ...prev.routines,
          {
            ...routine,
            fullRoutineId
          }
        ]
      };
    });
  };

  const handleFinishImport = async () => {
    if (selection.routines.length === 0) return;
    setIsImporting(true);
    setError(null);

    const failures = [];
    let importedCount = 0;

    for (const routine of selection.routines) {
      try {
        await udfApi.createUDF({
          name: routine.routine_id,
          routine_id: routine.fullRoutineId,
          signature: 'placeholder',
          source: 'bigquery',
          language: routine.language || 'sql'
        });
        importedCount += 1;
      } catch (err) {
        failures.push(routine.routine_id);
        console.error(`Failed to import routine ${routine.routine_id}:`, err);
      }
    }

    if (importedCount > 0 && onImportComplete) {
      onImportComplete(importedCount);
    }

    if (failures.length > 0) {
      setError(
        failures.length === selection.routines.length
          ? 'Failed to import selected routines. They may already exist.'
          : `Imported ${importedCount} routine(s), but ${failures.length} failed.`
      );
      setIsImporting(false);
      return;
    }

    setIsImporting(false);
    onClose();
    resetModal();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-950 w-full max-w-lg rounded-[40px] shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">
        <div className="p-8 border-b border-gray-50 dark:border-gray-900/50 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            {step !== 'select' && (
              <button onClick={handleBack} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-xl transition-colors">
                <ArrowLeft size={20} />
              </button>
            )}
            <div>
              <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tighter leading-none">
                {step === 'select' && 'Add Function'}
                {step === 'bigquery-auth' && 'Connect BigQuery'}
                {step === 'bigquery-dataset' && 'Select Dataset'}
                {step === 'bigquery-routine' && 'Select Routines'}
                {step === 'bigquery-confirm' && 'Review Import'}
              </h3>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                {step === 'select' && 'Choose how you want to import functions'}
                {step === 'bigquery-auth' && 'Authenticate via Google SSO'}
                {step === 'bigquery-dataset' && 'Choose a BigQuery dataset'}
                {step === 'bigquery-routine' && 'Select routines to import'}
                {step === 'bigquery-confirm' && 'Verify selected routines before adding'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mx-8 mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/50 rounded-2xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2 shrink-0">
            <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[10px] font-black text-red-600 dark:text-red-400 uppercase tracking-widest">Error</p>
              <p className="text-xs font-bold text-red-500 dark:text-red-300 mt-1">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="p-1 hover:bg-red-100 dark:hover:bg-red-800 rounded-lg transition-colors">
              <X size={14} className="text-red-400" />
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar min-h-[300px]">
          {step === 'select' && (
            <div className="space-y-2">
              <button
                onClick={handleBQLanding}
                className="w-full flex items-center gap-4 p-5 rounded-3xl transition-all border border-transparent text-left hover:bg-gray-50 dark:hover:bg-gray-900 hover:border-gray-100 dark:hover:border-gray-800 group"
              >
                <div className="p-4 rounded-2xl transition-transform shadow-sm group-hover:scale-110 bg-blue-50 dark:bg-blue-900/20 text-blue-600">
                  <Database size={28} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight">Google BigQuery</span>
                    {bqStatus?.connected && !bqStatus.billing_project_id && (
                      <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-100/50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg">
                        <AlertCircle size={10} />
                        <span className="text-[8px] font-black uppercase tracking-widest">Setup Required</span>
                      </div>
                    )}
                  </div>
                  <p className="text-[10px] text-gray-500 font-medium mt-0.5">Import routines from your cloud data warehouse</p>
                </div>
                <ChevronRight size={20} className="text-gray-300 group-hover:text-blue-500 transition-colors" />
              </button>
              <button
                disabled
                className="w-full flex items-center gap-4 p-5 rounded-3xl transition-all border border-transparent text-left opacity-50 cursor-not-allowed"
              >
                <div className="p-4 rounded-2xl shadow-sm bg-gray-50 dark:bg-gray-900/20 text-gray-400">
                  <Upload size={28} />
                </div>
                <div className="flex-1">
                  <span className="text-sm font-black text-gray-400 dark:text-gray-500 uppercase tracking-tight">Import from local file</span>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 font-medium mt-0.5">SQL, JavaScript</p>
                </div>
              </button>
            </div>
          )}

          {step === 'bigquery-auth' && (
            <div className="h-full flex flex-col items-center justify-center p-8 space-y-8">
              <div className="relative">
                <div className="absolute inset-0 bg-blue-500/20 blur-3xl rounded-full" />
                <div className="relative p-8 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-[40px] shadow-sm">
                  <ShieldCheck size={48} />
                </div>
              </div>
              <div className="text-center">
                <h4 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight mb-2">Google Cloud Authentication</h4>
                <p className="text-[11px] text-gray-500 font-medium max-w-[280px] mx-auto leading-relaxed">
                  We need your permission to access your BigQuery routines.
                </p>
              </div>
              <button
                onClick={handleBQAuth}
                className="w-full flex items-center justify-center gap-3 bg-blue-600 text-white p-5 rounded-[24px] font-black uppercase tracking-widest text-[11px] hover:bg-blue-700 transition-all shadow-xl shadow-blue-600/20 active:scale-[0.98]"
              >
                Connect Google Account
                <ExternalLink size={16} />
              </button>
            </div>
          )}

          {step === 'bigquery-dataset' && (
            <div className="space-y-3">
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-500 transition-colors" size={18} />
                <input
                  type="text"
                  placeholder="Filter datasets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-900 border border-transparent focus:border-emerald-500/50 rounded-2xl text-xs font-bold outline-none transition-all"
                />
              </div>
              <div className="space-y-1">
                {isLoading ? (
                  <div className="py-12 flex flex-col items-center justify-center gap-4">
                    <div className="w-8 h-8 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Loading datasets...</span>
                  </div>
                ) : filteredDatasets.length === 0 ? (
                  <div className="py-12 text-center text-gray-400 text-[10px] font-bold uppercase tracking-widest">No datasets found</div>
                ) : filteredDatasets.map((dataset) => (
                  <button
                    key={dataset.dataset_id}
                    onClick={() => handleDatasetSelect(dataset)}
                    className="w-full flex items-center justify-between p-4 rounded-2xl hover:bg-blue-50 dark:hover:bg-blue-900/20 group transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white dark:bg-gray-800 rounded-xl text-blue-600 shadow-sm">
                        <Database size={16} />
                      </div>
                      <div className="text-left font-black text-xs text-gray-900 dark:text-white uppercase tracking-tight">
                        {dataset.dataset_id}
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-gray-300 group-hover:text-blue-500 transition-colors" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 'bigquery-routine' && (
            <div className="space-y-3">
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-500 transition-colors" size={18} />
                <input
                  type="text"
                  placeholder="Search routines..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-900 border border-transparent focus:border-emerald-500/50 rounded-2xl text-xs font-bold outline-none transition-all"
                />
              </div>
              <div className="space-y-1">
                {isLoading ? (
                  <div className="py-12 flex flex-col items-center justify-center gap-4">
                    <div className="w-8 h-8 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Loading routines...</span>
                  </div>
                ) : filteredRoutines.length === 0 ? (
                  <div className="py-12 text-center text-gray-400 text-[10px] font-bold uppercase tracking-widest">No routines found</div>
                ) : filteredRoutines.map((routine) => {
                  const fullRoutineId = `${selection.project.project_id}.${selection.dataset.dataset_id}.${routine.routine_id}`;
                  const isSelected = selection.routines.some(r => r.fullRoutineId === fullRoutineId);
                  const isAlreadyImported = importedRoutineIds.has(fullRoutineId);

                  return (
                    <button
                      key={fullRoutineId}
                      onClick={() => toggleRoutineSelection(routine)}
                      disabled={isAlreadyImported}
                      className={cn(
                        "w-full flex items-center justify-between p-4 rounded-2xl group transition-all border",
                        isAlreadyImported
                          ? "opacity-60 cursor-not-allowed border-emerald-100 dark:border-emerald-900/30 bg-emerald-50/50 dark:bg-emerald-900/10"
                          : isSelected
                            ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
                            : "hover:bg-gray-50 dark:hover:bg-gray-900 border-transparent hover:border-gray-100 dark:hover:border-gray-800"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "p-2 rounded-xl shadow-sm transition-colors",
                          isAlreadyImported
                            ? "bg-emerald-600 text-white"
                            : isSelected
                              ? "bg-blue-600 text-white"
                              : "bg-white dark:bg-gray-800 text-gray-400 group-hover:text-blue-600"
                        )}>
                          <FileText size={16} />
                        </div>
                        <div className="text-left">
                          <div className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-tight">{routine.routine_id}</div>
                          <div className="text-[10px] text-gray-400 font-bold uppercase">
                            {isAlreadyImported ? 'Already imported' : routine.type}
                          </div>
                        </div>
                      </div>
                      {(isSelected || isAlreadyImported) && (
                        <div className={cn(
                          "w-6 h-6 rounded-full flex items-center justify-center text-white animate-in zoom-in duration-200",
                          isAlreadyImported ? "bg-emerald-600" : "bg-blue-600"
                        )}>
                          <Check size={14} strokeWidth={3} />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 'bigquery-confirm' && (
            <div className="space-y-2">
              <div className="mb-4 px-2">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Selected Routines</p>
              </div>
              {selection.routines.length === 0 ? (
                <div className="py-12 text-center text-gray-400 text-[10px] font-bold uppercase tracking-widest">
                  No routines selected
                </div>
              ) : (
                selection.routines.map((routine) => (
                  <div
                    key={routine.fullRoutineId}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-800"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white dark:bg-gray-800 rounded-xl text-blue-600 shadow-sm">
                        <FileText size={16} />
                      </div>
                      <div className="text-left">
                        <div className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-tight">
                          {routine.routine_id}
                        </div>
                        <div className="text-[8px] text-gray-400 font-bold uppercase tracking-widest">
                          {selection.project.project_id} • {selection.dataset.dataset_id}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleRoutineSelection(routine)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {selection.routines.length > 0 && (step === 'bigquery-routine' || step === 'bigquery-confirm') && (
          <div className="p-8 border-t border-gray-50 dark:border-gray-900/50 bg-gray-50/30 dark:bg-gray-900/30 shrink-0">
            {step === 'bigquery-routine' ? (
              <button
                onClick={() => setStep('bigquery-confirm')}
                className="w-full bg-emerald-600 text-white p-5 rounded-[24px] font-black uppercase tracking-widest text-[11px] hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-600/20 active:scale-[0.98] flex items-center justify-center gap-2"
              >
                Add {selection.routines.length} {selection.routines.length === 1 ? 'Routine' : 'Routines'}
                <Check size={16} strokeWidth={3} />
              </button>
            ) : (
              <button
                onClick={handleFinishImport}
                disabled={isImporting}
                className={cn(
                  "w-full bg-emerald-600 text-white p-5 rounded-[24px] font-black uppercase tracking-widest text-[11px] hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-600/20 active:scale-[0.98] flex items-center justify-center gap-2",
                  isImporting && "opacity-70 cursor-wait"
                )}
              >
                {isImporting ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Importing Routines...
                  </>
                ) : (
                  <>
                    Import Selected Routines
                    <Check size={16} strokeWidth={3} />
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FunctionImportModal;
